import { Link } from "react-router-dom";
import { ArrowRight, Shield, Zap, BarChart3, Share2, MessageSquare, Lock, Target, ChevronRight } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1bc1e0bc-6716-4e3a-9c11-cd3b13fbaefa/images/dbe8c6351418ace6f90cd63fd643b1e93498657565609ea0eae3fc4c7c8ce11d.png";

const LEAGUES = [
  "Premier League", "La Liga", "Serie A", "Bundesliga", "Ligue 1",
  "Major League Soccer", "Saudi Pro League", "CAF Competitions", "Asian Leagues",
];

const SYSTEM_VALUES = [
  { icon: Shield, text: "Structured professional player profile" },
  { icon: MessageSquare, text: "Clear football identity" },
  { icon: Zap, text: "Guided improvement" },
  { icon: BarChart3, text: "Profile strength scoring" },
  { icon: Target, text: "Targeted improvement actions" },
  { icon: Share2, text: "Shareable profile for clubs and scouts" },
];

export default function Landing() {
  return (
    <div className="bg-[#0A0A0A]" data-testid="landing-page">

      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-[#F5F5F5] leading-[0.95] mb-6 animate-fade-in-up" data-testid="hero-headline">
              IF YOU ARE NOT<br />POSITIONED PROPERLY,<br /><span className="text-[#D4AF37]">YOU ARE NOT<br />BEING ASSESSED</span>
            </h1>
            <p className="text-base text-[#A3A3A3] mb-10 max-w-md leading-relaxed animate-fade-in-up stagger-1">
              Most players are guessing.<br />
              That's the problem.
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in-up stagger-2">
              <Link to="/get-profile" className="btn-gold inline-flex items-center gap-2 text-sm" data-testid="hero-cta-profile">
                GET YOUR PROFILE <ArrowRight size={16} />
              </Link>
              <Link to="/scout-apply" className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="hero-cta-scout">
                SCOUT ACCESS
              </Link>
            </div>
            <p className="text-xs text-[#777] mt-4 animate-fade-in-up stagger-3">Takes a few minutes. Fully guided.</p>
            <p className="text-[10px] text-[#555] mt-2 animate-fade-in-up stagger-3">Used by players serious about being seen.</p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — GLOBAL LEAGUES */}
      <section className="py-20 border-t border-[#262626]" data-testid="global-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="overline mb-4">GLOBAL REACH</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5]">
              OPERATING ACROSS THE GLOBAL GAME
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-0">
            {LEAGUES.map((league, i) => (
              <div key={i} className="px-6 py-4 border border-[#262626] text-center" data-testid={`league-${i}`}>
                <span className="text-sm text-[#E5E5E5] font-medium tracking-wide">{league}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#A3A3A3] text-center mt-8 max-w-xl mx-auto">
            Players in this network come from these environments.
          </p>
        </div>
      </section>

      {/* SECTION 3 — PROBLEM */}
      <section className="py-20 border-t border-[#262626]" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="overline mb-4">THE PROBLEM</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-8">
              MOST PLAYERS<br />DON'T KNOW
            </h2>
            <div className="space-y-4">
              {[
                "Who to contact",
                "What to send",
                "How they are being assessed",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-[#E5E5E5]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — CORE POSITION */}
      <section className="py-24 border-t border-[#262626] bg-[#0E0E0E]" data-testid="core-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="overline mb-4">THE SOLUTION</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-6">
            THIS IS NOT JUST<br />A PROFILE
          </h2>
          <p className="text-base text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
            This is how players are structured and positioned properly.
          </p>
        </div>
      </section>

      {/* SECTION 5 — SYSTEM VALUE */}
      <section className="py-20 border-t border-[#262626]" data-testid="system-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="overline mb-4">THE SYSTEM</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5]">
              A COMPLETE SYSTEM FOR<br />PLAYER VISIBILITY
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 max-w-4xl mx-auto">
            {SYSTEM_VALUES.map((item, i) => (
              <div key={i} className="p-6 border border-[#262626] flex items-start gap-3" data-testid={`system-${i}`}>
                <item.icon size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-[#E5E5E5] leading-relaxed">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — AUTHORITY */}
      <section className="py-20 border-t border-[#262626]" data-testid="authority-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="overline mb-4">THE NETWORK</div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-6">
            SCOUTS USE ELITE SCOUTING<br />NETWORK TO REVIEW PLAYERS
          </h2>
          <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
            Clear. Structured. Easy to assess.
          </p>
          <p className="text-sm text-[#E5E5E5] leading-relaxed max-w-xl mx-auto mt-6">
            Use it inside the network…<br />or send it directly.
          </p>
        </div>
      </section>

      {/* SECTION 7 — CORE STATEMENT */}
      <section className="py-16 border-t border-[#262626] bg-[#0E0E0E]" data-testid="statement-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-base sm:text-lg text-[#F5F5F5] leading-relaxed font-bold">
            Players here are not just listed &mdash; they are positioned to be seen.
          </p>
        </div>
      </section>

      {/* SECTION 8 — EXCLUSIVITY */}
      <section className="py-20 border-t border-[#262626]" data-testid="exclusivity-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock size={16} className="text-[#D4AF37]" />
            <div className="overline">ACCESS</div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-6">
            PRIVATE NETWORK
          </h2>
          <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-md mx-auto">
            Players enter by building a structured profile.<br />
            Scouts apply and are approved.
          </p>
        </div>
      </section>

      {/* SECTION 9 — PRESSURE LINE */}
      <section className="py-16 border-t border-[#262626]" data-testid="pressure-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm text-[#A3A3A3] leading-relaxed">
            If you are not presented properly, you are easy to overlook.
          </p>
        </div>
      </section>

      {/* SECTION 10 — FINAL CTA */}
      <section className="py-24 border-t border-[#262626] bg-[#0E0E0E]" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-base text-[#A3A3A3] mb-8 leading-relaxed">
            If you are serious about football, you need to be seen properly.
          </p>
          <Link to="/get-profile" className="btn-gold inline-flex items-center gap-2 text-base" data-testid="final-cta">
            GET YOUR PROFILE <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-5">
          <img src="https://customer-assets.emergentagent.com/job_elite-scout-mvp/artifacts/uw8m7f8v_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo.png" alt="Elite Scouting Network" className="h-14 w-14 rounded-full opacity-80" />
          <span className="text-[#F5F5F5] font-bold text-sm tracking-tight uppercase" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Elite Scouting Network</span>
          <div className="flex gap-6 text-xs">
            <Link to="/get-profile" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Get Profile</Link>
            <Link to="/directory" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Directory</Link>
            <Link to="/scout-apply" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Scout Access</Link>
            <Link to="/pricing" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Pricing</Link>
          </div>
          <span className="text-[#555] text-[10px] tracking-wider">All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
